import React, { FC, useContext, useRef, useState } from 'react';

// Components
import Form from '../../components/form/form';
import StudentSelector from '../../components/student-selector/student-selector';

// Form Data
import { formDataActivityCreate } from '../../form-data/form-data-activity-create';

// Helpers
import {
  deleteAttachment,
  handleFormOnChange,
  handleStudentChange,
  isFormComplete,
} from '../../helpers/activity-helper';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import FileStorageService from '../../services/file-storage-service';

export const useActivityForm = (initialData: () => ActivityFormData) => {
  const [data, setData] = useState<ActivityFormData>(initialData);
  const [validations, setValidations] = useState({});
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // O modal de agendamento guarda a função de envio de quando foi aberto, então
  // tanto a trava quanto os dados enviados vêm de refs, não do estado capturado.
  const submitting = useRef(false);
  const latest = useRef(data);
  latest.current = data;

  // Um duplo clique criaria duas atividades, cada uma com o seu push.
  const submitOnce = (send: (current: ActivityFormData) => Promise<void>): Promise<void> => {
    if (submitting.current) return Promise.resolve();
    submitting.current = true;
    setIsSubmitting(true);

    return send(latest.current).catch((error) => {
      submitting.current = false;
      setIsSubmitting(false);
      throw error;
    });
  };

  return {
    data,
    setData,
    validations,
    setValidations,
    isLoadingAttachment,
    setIsLoadingAttachment,
    allowSubmit: isFormComplete(data, validations) && !isSubmitting,
    submitOnce,
  };
};

type ActivityForm = ReturnType<typeof useActivityForm>;

// BaseContentContainer distribui os filhos por posição, então o formulário e o
// seletor de alunos são dois componentes, um para cada coluna.
export const ActivityFormInputs: FC<{
  form: ActivityForm;
  activity?: ActivityDto;
  fileStorageService: FileStorageService;
}> = ({ form, activity, fileStorageService }) => {
  const { setToastContent } = useContext(GeneralContext);
  const { data, setData, validations, setValidations } = form;

  return (
    <Form
      formData={formDataActivityCreate(activity)}
      validations={validations}
      handleOnChange={(evt) =>
        handleFormOnChange(evt, {
          data,
          setData,
          setValidations,
          setIsLoadingAttachment: form.setIsLoadingAttachment,
          fileStorageService,
          setToastContent,
        })
      }
      attachments={data.formData.attachments.map((attachment) => ({
        id: attachment.fileId,
        originalName: attachment.name,
      }))}
      isLoadingAttachment={form.isLoadingAttachment}
      deleteAttachment={(fileId: string) => deleteAttachment(fileId, setData, setValidations)}
    />
  );
};

export const ActivityRecipients: FC<{
  form: ActivityForm;
  classroomList: ClassroomStudents[];
}> = ({ form, classroomList }) => {
  const { data, setData } = form;

  const selector = (
    <StudentSelector
      classrooms={classroomList}
      handleStudentChange={(selectedClassrooms: number[], selectedStudents: number[]) =>
        handleStudentChange(selectedClassrooms, selectedStudents, setData)
      }
      initialClassrooms={data.classData}
      initialStudents={data.studentData}
    />
  );

  if (data.unmatchedStudentUuids.length === 0) return selector;

  // O seletor ocupa 100% da altura e rola por dentro; numa coluna flex ele
  // encolhe para dar lugar ao aviso em vez de transbordar.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <p style={{ padding: '24px 40px 0', flexShrink: 0 }}>
        {data.unmatchedStudentUuids.length} aluno(s) que receberam esta atividade não estão nas
        turmas listadas e continuam como destinatários.
      </p>
      {selector}
    </div>
  );
};
